import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Empty } from "../components/common";
import { toast } from "sonner";
import { Users, Trash2, Shield, Building2, Briefcase } from "lucide-react";

const ROLE_BADGE = {
  admin: "bg-[#1E252B] text-white",
  buyer: "bg-[#00427A]/10 text-[#00427A]",
  smme: "bg-emerald-50 text-emerald-700",
};
const ROLE_ICON = { admin: Shield, buyer: Briefcase, smme: Building2 };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their profile, documents and responses.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User account deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Administrator" title="User Accounts"
        subtitle="Manage all portal accounts. Deleting a user removes their data." />

      {users.length === 0 ? (
        <Empty icon={Users} title="No users" />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Role</th>
                <th className="px-5 py-3 font-semibold">Mine</th>
                <th className="px-5 py-3 font-semibold">Joined</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const Icon = ROLE_ICON[u.role] || Users;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                    <td className="px-5 py-4 font-medium text-slate-900">{u.name} {u.surname}</td>
                    <td className="px-5 py-4 text-slate-600">{u.email}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${ROLE_BADGE[u.role]}`}>
                        <Icon className="h-3 w-3" /> {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{u.mine_name || "—"}</td>
                    <td className="px-5 py-4 text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-4 text-right">
                      {u.role !== "admin" && (
                        <button data-testid={`admin-delete-user-button-${u.id}`} onClick={() => remove(u)}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md">
                          <Trash2 className="h-4 w-4" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
